import type { CSSProperties, ComponentType, ReactNode } from 'react';
import { useEffect, useState } from 'react';
import {
  IconLayoutSidebarLeftCollapse,
  IconLayoutSidebarLeftExpand,
} from '@tabler/icons-react';
import { Box } from '../core/atoms/Box';
import { Stack } from '../core/atoms/Stack';
import { Group } from '../core/atoms/Group';
import { Text } from '../core/atoms/Text';
import { Title } from '../core/atoms/Title';
import { Avatar } from '../core/atoms/Avatar';
import { ActionIcon } from '../core/atoms/ActionIcon';
import { Menu } from '../core/atoms/Menu';
import { Divider } from '../core/atoms/Divider';
import { Burger, Tooltip, UnstyledButton } from '@mantine/core';
import { Button } from '../core/atoms/Button/Button';
import { BrandLogo } from './BrandLogo';
import { usePlatform } from '../hooks/usePlatform';

const SIDEBAR_EXPANDED = 248;
const SIDEBAR_COLLAPSED = 72;
const HEADER_HEIGHT = 60;
const FOOTER_HEIGHT = 44;
const COLLAPSE_STORAGE_KEY = 'vipso-shell-sidebar-collapsed';

export type ShellNavItem = {
  to: string;
  label: string;
  icon?: ComponentType<{ size?: number | string; stroke?: number }>;
  end?: boolean;
  /** Extra path prefixes that should mark this item active in the floating nav */
  activePatterns?: string[];
};

export type ShellProfileLink = {
  label: string;
  to?: string;
  onClick?: () => void;
  color?: string;
};

export type ShellLinkProps = {
  to: string;
  end?: boolean;
  children: ReactNode | ((opts: { isActive: boolean }) => ReactNode);
  style?: (opts: { isActive: boolean }) => CSSProperties;
  onClick?: () => void;
};

export type DashboardShellProps = {
  navItems: ShellNavItem[];
  /** Mobile floating bottom bar items (defaults to first 4 of navItems). */
  mobileNavItems?: ShellNavItem[];
  /** Items shown in the mobile slide-out drawer (defaults to navItems). */
  mobileDrawerItems?: ShellNavItem[];
  /** Current route pathname — used with activePatterns on floating nav items. */
  currentPath?: string;
  LinkComponent: ComponentType<ShellLinkProps>;
  children: ReactNode;
  brandTitle?: string;
  brandSubtitle?: string;
  logoUrl?: string | null;
  orgName?: string | null;
  user?: { name?: string | null; email?: string | null; phone?: string | null } | null;
  profileLinks?: ShellProfileLink[];
  onLogout?: () => void;
  /** When set, mobile header org name and drawer expose switch-org. */
  onOrgSwitch?: () => void;
  /** Label for switch-org control (defaults to "Switch organization"). */
  orgSwitchLabel?: string;
  footerText?: string;
  headerExtra?: ReactNode;
  /** Full-width running notification strip (payment warnings, maintenance, etc.). */
  notificationBanner?: ReactNode;
  /** Force mobile chrome (floating nav + drawer). Auto when platform=mobile or narrow viewport. */
  forceMobile?: boolean;
};

function useIsNarrow(breakpoint = 900) {
  const [narrow, setNarrow] = useState(() =>
    typeof window !== 'undefined' ? window.innerWidth < breakpoint : false,
  );
  useEffect(() => {
    const onResize = () => setNarrow(window.innerWidth < breakpoint);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [breakpoint]);
  return narrow;
}

function useSidebarCollapsed() {
  const [collapsed, setCollapsed] = useState(() => {
    if (typeof window === 'undefined') return false;
    return window.localStorage.getItem(COLLAPSE_STORAGE_KEY) === '1';
  });

  function toggle() {
    setCollapsed((prev) => {
      const next = !prev;
      try {
        window.localStorage.setItem(COLLAPSE_STORAGE_KEY, next ? '1' : '0');
      } catch {
        /* ignore */
      }
      return next;
    });
  }

  return { collapsed, toggle };
}

function defaultFooterText() {
  const year = new Date().getFullYear();
  return `Powered and Maintained by Spurnest Techlabs, Copyrights reserved © ${year}`;
}

function FloatingNavItem({
  item,
  LinkComponent,
  currentPath,
}: {
  item: ShellNavItem;
  LinkComponent: ComponentType<ShellLinkProps>;
  currentPath?: string;
}) {
  const IconCmp = item.icon;
  const extraActive = currentPath
    ? (item.activePatterns ?? []).some((p) => currentPath.startsWith(p))
    : false;

  return (
    <LinkComponent
      to={item.to}
      end={item.end ?? (item.to === '/' || item.to === '/dashboard')}
      style={({ isActive: navActive }) => {
        const isActive = navActive || extraActive;
        return {
          flex: isActive ? 1.35 : 0,
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
          justifyContent: 'center',
          gap: isActive ? 8 : 0,
          padding: isActive ? '10px 14px' : '10px',
          borderRadius: 16,
          textDecoration: 'none',
          color: isActive ? 'var(--mantine-color-brand-7)' : 'var(--mantine-color-navy-6)',
          background: isActive ? 'var(--mantine-color-brand-0)' : 'transparent',
          fontSize: 12,
          fontWeight: isActive ? 700 : 500,
          transition: 'background 140ms ease, color 140ms ease, flex 140ms ease, padding 140ms ease',
          minWidth: isActive ? 0 : 44,
          width: isActive ? undefined : 44,
          height: 44,
          boxSizing: 'border-box' as const,
        };
      }}
    >
      {({ isActive: navActive }) => {
        const isActive = navActive || extraActive;
        return (
          <>
            {IconCmp ? <IconCmp size={22} stroke={1.75} /> : null}
            {isActive ? (
              <span
                style={{
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  maxWidth: '100%',
                }}
              >
                {item.label}
              </span>
            ) : null}
          </>
        );
      }}
    </LinkComponent>
  );
}

function NavRow({
  item,
  LinkComponent,
  collapsed,
  onNavigate,
}: {
  item: ShellNavItem;
  LinkComponent: ComponentType<ShellLinkProps>;
  collapsed?: boolean;
  onNavigate?: () => void;
}) {
  const IconCmp = item.icon;
  const link = (
    <LinkComponent
      to={item.to}
      end={item.end ?? (item.to === '/' || item.to === '/dashboard')}
      onClick={onNavigate}
      style={({ isActive }) => ({
        display: 'flex',
        alignItems: 'center',
        justifyContent: collapsed ? 'center' : 'flex-start',
        gap: collapsed ? 0 : 12,
        flexDirection: 'row',
        padding: collapsed ? '12px 0' : '10px 14px',
        borderRadius: 10,
        textDecoration: 'none',
        color: isActive ? 'var(--mantine-color-brand-7)' : 'var(--mantine-color-navy-8)',
        background: isActive ? 'var(--mantine-color-brand-0)' : 'transparent',
        fontSize: 14,
        fontWeight: isActive ? 650 : 500,
        width: '100%',
        transition: 'background 120ms ease, color 120ms ease',
      })}
    >
      <>
        {IconCmp ? <IconCmp size={18} stroke={1.75} /> : null}
        {!collapsed ? <span>{item.label}</span> : null}
      </>
    </LinkComponent>
  );

  if (collapsed) {
    return (
      <Tooltip label={item.label} position="right" withArrow>
        <Box>{link}</Box>
      </Tooltip>
    );
  }

  return link;
}

function ProfileMenu({
  user,
  orgName,
  profileLinks = [],
  onLogout,
}: {
  user?: DashboardShellProps['user'];
  orgName?: string | null;
  profileLinks?: ShellProfileLink[];
  onLogout?: () => void;
}) {
  const initials = (user?.name || 'U')
    .split(' ')
    .map((p) => p[0])
    .join('')
    .slice(0, 2)
    .toUpperCase();

  return (
    <Menu shadow="md" width={240} position="bottom-end">
      <Menu.Target>
        <ActionIcon
          variant="subtle"
          radius="xl"
          size={40}
          aria-label="Open profile menu"
          style={{ padding: 0 }}
        >
          <Avatar radius="xl" color="brand" size={36}>
            {initials}
          </Avatar>
        </ActionIcon>
      </Menu.Target>
      <Menu.Dropdown>
        <Menu.Label>
          <Stack gap={2}>
            <Text size="sm" fw={600} c="navy.9">
              {user?.name || 'Account'}
            </Text>
            {orgName ? (
              <Text size="xs" c="dimmed">
                {orgName}
              </Text>
            ) : null}
            {user?.email || user?.phone ? (
              <Text size="xs" c="dimmed">
                {user.email || user.phone}
              </Text>
            ) : null}
          </Stack>
        </Menu.Label>
        <Menu.Divider />
        {profileLinks.map((link) => (
          <Menu.Item key={link.label} onClick={link.onClick} color={link.color}>
            {link.label}
          </Menu.Item>
        ))}
        {onLogout ? (
          <>
            <Menu.Divider />
            <Menu.Item color="red" onClick={onLogout}>
              Sign out
            </Menu.Item>
          </>
        ) : null}
      </Menu.Dropdown>
    </Menu>
  );
}

export function DashboardShell({
  navItems,
  mobileNavItems,
  mobileDrawerItems,
  currentPath,
  LinkComponent,
  children,
  brandTitle = 'VIPSo',
  logoUrl,
  orgName,
  user,
  profileLinks = [],
  onLogout,
  onOrgSwitch,
  orgSwitchLabel = 'Switch organization',
  footerText,
  headerExtra,
  notificationBanner,
  forceMobile,
}: DashboardShellProps) {
  const platform = usePlatform();
  const narrow = useIsNarrow(960);
  const isMobile = forceMobile ?? (platform === 'mobile' || narrow);
  const orgSwitchShort =
    orgSwitchLabel.length > 18 ? 'Switch' : orgSwitchLabel;
  const [drawerOpen, setDrawerOpen] = useState(false);
  const { collapsed, toggle } = useSidebarCollapsed();
  const sidebarWidth = collapsed ? SIDEBAR_COLLAPSED : SIDEBAR_EXPANDED;
  const resolvedFooter = footerText ?? defaultFooterText();
  /** Prefer live org name (mobile header source) so desktop wordmark never stays VIPSo. */
  const headerWordmark = (orgName?.trim() || brandTitle || 'VIPSo').trim();
  const floatingItems = mobileNavItems?.length
    ? mobileNavItems
    : navItems.slice(0, 4);
  const drawerNavItems =
    mobileDrawerItems?.length ? mobileDrawerItems : navItems;

  function renderNav(items: ShellNavItem[], iconOnly: boolean) {
    return (
      <Stack gap={4} style={{ flex: 1, overflow: 'auto', minHeight: 0 }}>
        {!iconOnly ? (
          <Text size="xs" tt="uppercase" fw={700} c="dimmed" px="sm" mb={4}>
            Menu
          </Text>
        ) : null}
        {items.map((item) => (
          <NavRow
            key={item.to}
            item={item}
            LinkComponent={LinkComponent}
            collapsed={iconOnly}
            onNavigate={() => setDrawerOpen(false)}
          />
        ))}
      </Stack>
    );
  }

  const desktopSidebarNav = renderNav(navItems, collapsed);
  const mobileDrawerNav = renderNav(drawerNavItems, false);

  const sidebarUser = (
    <Box>
      <Divider mb="sm" />
      {collapsed && !isMobile ? (
        <Group justify="center">
          <Avatar radius="xl" color="brand" size={36}>
            {(user?.name || 'U').slice(0, 1).toUpperCase()}
          </Avatar>
        </Group>
      ) : (
        <Group gap="sm" wrap="nowrap">
          <Avatar radius="xl" color="brand" size={36}>
            {(user?.name || 'U').slice(0, 1).toUpperCase()}
          </Avatar>
          <Stack gap={0} style={{ minWidth: 0, flex: 1 }}>
            <Text size="sm" fw={600} truncate>
              {user?.name || 'User'}
            </Text>
            <Text size="xs" c="dimmed" truncate>
              {orgName || user?.email || user?.phone || '—'}
            </Text>
          </Stack>
        </Group>
      )}
    </Box>
  );

  return (
    <Box
      style={{
        height: '100vh',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        background: 'var(--mantine-color-gray-0, #fafafa)',
      }}
    >
      {/* Web: full-bleed strip above sidebar + header (100% viewport width) */}
      {!isMobile && notificationBanner ? notificationBanner : null}

      <Box
        style={{
          flex: 1,
          minHeight: 0,
          display: 'flex',
          overflow: 'hidden',
        }}
      >
        {/* Desktop sidebar */}
        {!isMobile ? (
          <Box
            component="aside"
            style={{
              width: sidebarWidth,
              flexShrink: 0,
              height: '100%',
              borderRight: '1px solid var(--mantine-color-gray-3)',
              background: '#fff',
              padding: collapsed ? '16px 8px' : 16,
              display: 'flex',
              flexDirection: 'column',
              gap: 16,
              transition: 'width 180ms ease',
              overflow: 'hidden',
            }}
          >
            {desktopSidebarNav}
            {sidebarUser}
          </Box>
        ) : null}

        {/* Right column: header + main + footer */}
        <Box
          style={{
            flex: 1,
            minWidth: 0,
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <Box
            component="header"
            style={{
              // Keep 60px content row; pad above for Dynamic Island / notch.
              minHeight: HEADER_HEIGHT,
              height: `calc(${HEADER_HEIGHT}px + env(safe-area-inset-top, 0px))`,
              paddingTop: 'env(safe-area-inset-top, 0px)',
              boxSizing: 'border-box',
              flexShrink: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              paddingLeft: 16,
              paddingRight: 16,
              borderBottom: '1px solid var(--mantine-color-gray-3)',
              background: '#fff',
              zIndex: 40,
            }}
          >
            <Group gap="sm" style={{ flexShrink: 0 }}>
              {isMobile ? (
                <Burger
                  opened={drawerOpen}
                  onClick={() => setDrawerOpen((v) => !v)}
                  size="sm"
                  aria-label="Toggle navigation"
                />
              ) : (
                <Tooltip label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'} withArrow>
                  <ActionIcon
                    variant="subtle"
                    color="navy"
                    size="lg"
                    aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
                    onClick={toggle}
                  >
                    {collapsed ? (
                      <IconLayoutSidebarLeftExpand size={20} stroke={1.75} />
                    ) : (
                      <IconLayoutSidebarLeftCollapse size={20} stroke={1.75} />
                    )}
                  </ActionIcon>
                </Tooltip>
              )}
              <BrandLogo
                logoUrl={logoUrl}
                withWordmark={!isMobile}
                wordmark={headerWordmark}
                size={isMobile ? 28 : 32}
              />
            </Group>

            {isMobile && orgName ? (
              onOrgSwitch ? (
                <UnstyledButton
                  onClick={onOrgSwitch}
                  aria-label={orgSwitchLabel}
                  style={{
                    flex: 1,
                    minWidth: 0,
                    padding: '0 8px',
                    textAlign: 'center',
                  }}
                >
                  <Text
                    size="sm"
                    fw={600}
                    c="navy.8"
                    style={{
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {orgName}
                  </Text>
                  <Text size="xs" c="dimmed" style={{ lineHeight: 1.2 }}>
                    {orgSwitchShort}
                  </Text>
                </UnstyledButton>
              ) : (
                <Text
                  size="sm"
                  fw={600}
                  c="navy.8"
                  style={{
                    flex: 1,
                    textAlign: 'center',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    padding: '0 8px',
                  }}
                >
                  {orgName}
                </Text>
              )
            ) : (
              <Box style={{ flex: 1 }} />
            )}

            <Group gap="sm" style={{ flexShrink: 0 }}>
              {headerExtra}
              <ProfileMenu
                user={user}
                orgName={orgName}
                profileLinks={profileLinks}
                onLogout={onLogout}
              />
            </Group>
          </Box>

          {/* Mobile/tablet: strip below header, still full viewport width */}
          {isMobile && notificationBanner ? notificationBanner : null}

          {/* Mobile drawer */}
          {isMobile && drawerOpen ? (
            <>
              <Box
                onClick={() => setDrawerOpen(false)}
                style={{
                  position: 'fixed',
                  inset: 0,
                  background: 'rgba(0,14,33,0.45)',
                  zIndex: 50,
                }}
              />
              <Box
                style={{
                  position: 'fixed',
                  top: 0,
                  left: 0,
                  bottom: 0,
                  width: 'min(82vw, 300px)',
                  background: '#fff',
                  zIndex: 51,
                  paddingTop: 'calc(16px + env(safe-area-inset-top, 0px))',
                  paddingBottom: 'calc(16px + env(safe-area-inset-bottom, 0px))',
                  paddingLeft: 16,
                  paddingRight: 16,
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 16,
                  boxShadow: '8px 0 32px rgba(0,14,33,0.18)',
                }}
              >
                <BrandLogo logoUrl={logoUrl} wordmark={headerWordmark} size={34} />
                <Box style={{ flex: 1, minHeight: 0, overflow: 'auto' }}>{mobileDrawerNav}</Box>
                <Stack gap="xs">
                  {onOrgSwitch ? (
                    <Button
                      variant="light"
                      fullWidth
                      onClick={() => {
                        setDrawerOpen(false);
                        onOrgSwitch();
                      }}
                    >
                      {orgSwitchLabel}
                    </Button>
                  ) : null}
                  {profileLinks
                    .filter((link) => !onOrgSwitch || link.onClick !== onOrgSwitch)
                    .map((link) => (
                      <Button
                        key={link.label}
                        variant="default"
                        fullWidth
                        color={link.color}
                        onClick={() => {
                          setDrawerOpen(false);
                          link.onClick?.();
                        }}
                      >
                        {link.label}
                      </Button>
                    ))}
                  {onLogout ? (
                    <Button
                      variant="subtle"
                      color="red"
                      fullWidth
                      onClick={() => {
                        setDrawerOpen(false);
                        onLogout();
                      }}
                    >
                      Sign out
                    </Button>
                  ) : null}
                  {sidebarUser}
                </Stack>
              </Box>
            </>
          ) : null}

          <Box
            component="main"
            style={{
              flex: 1,
              minHeight: 0,
              overflow: 'auto',
              padding: isMobile
                ? '16px 16px calc(96px + env(safe-area-inset-bottom))'
                : 6,
              ...(!isMobile ? { background: 'var(--mantine-shadow-lg)' } : {}),
            }}
          >
            <Box style={{ background: 'var(--mantine-color-white)', padding: 16, borderRadius: 16, border: '1px solid var(--mantine-color-gray-3)' }}>
              {children}
            </Box>
          </Box>

          {!isMobile ? (
            <Box
              component="footer"
              style={{
                height: FOOTER_HEIGHT,
                flexShrink: 0,
                borderTop: '1px solid var(--mantine-color-gray-3)',
                padding: '0 24px',
                background: '#fff',
                display: 'flex',
                alignItems: 'center',
              }}
            >
              <Text size="xs" c="dimmed">
                {resolvedFooter}
              </Text>
            </Box>
          ) : null}
        </Box>
      </Box>

      {/* Mobile floating navbar */}
      {isMobile ? (
        <nav
          aria-label="Primary"
          style={{
            position: 'fixed',
            left: 40,
            right: 40,
            bottom: 'calc(12px + env(safe-area-inset-bottom))',
            zIndex: 45,
            display: 'flex',
            alignItems: 'center',
            gap: 4,
            padding: 6,
            borderRadius: 22,
            background: 'rgba(255,255,255,0.94)',
            border: '1px solid var(--mantine-color-gray-3)',
            boxShadow:
              '0 10px 28px rgba(0,14,33,0.14), 0 2px 8px rgba(0,14,33,0.06)',
            backdropFilter: 'blur(12px)',
            WebkitBackdropFilter: 'blur(12px)',
          }}
        >
          {floatingItems.map((item) => (
            <FloatingNavItem
              key={item.to}
              item={item}
              LinkComponent={LinkComponent}
              currentPath={currentPath}
            />
          ))}
        </nav>
      ) : null}
    </Box>
  );
}

export function AuthScreen({
  children,
  logoUrl,
  title,
  subtitle,
}: {
  children: ReactNode;
  logoUrl?: string | null;
  title?: string;
  subtitle?: string;
}) {
  return (
    <Box
      style={{
        minHeight: '100vh',
        display: 'grid',
        placeItems: 'center',
        paddingTop: 'calc(24px + env(safe-area-inset-top, 0px))',
        paddingBottom: 'calc(24px + env(safe-area-inset-bottom, 0px))',
        paddingLeft: 'calc(24px + env(safe-area-inset-left, 0px))',
        paddingRight: 'calc(24px + env(safe-area-inset-right, 0px))',
        background: `
          radial-gradient(1200px 500px at 10% -10%, rgba(204,32,46,0.12), transparent 55%),
          radial-gradient(900px 400px at 100% 0%, rgba(255,219,221,0.65), transparent 50%),
          ${VIPSO_CANVAS_FALLBACK}
        `,
      }}
    >
      <Box style={{ width: '100%', maxWidth: 420 }}>
        <Stack align="center" gap="lg" mb="lg">
          <BrandLogo logoUrl={logoUrl} size={48} />
          {title ? (
            <Stack gap={4} align="center">
              <Title order={2} ta="center" c="navy.9">
                {title}
              </Title>
              {subtitle ? (
                <Text size="sm" c="dimmed" ta="center">
                  {subtitle}
                </Text>
              ) : null}
            </Stack>
          ) : null}
        </Stack>
        {children}
      </Box>
    </Box>
  );
}

const VIPSO_CANVAS_FALLBACK = '#fafafa';
